export interface TankEmptyingInputs {
  tankDiameterMm: number;
  outletDiameterMm: number;
  initialLevelMm: number;
  dischargeCoefficient: number;
  gravity: number;
}

export interface TankEmptyingPoint {
  timeSeconds: number;
  liquidLevelMm: number;
  flowM3PerHour: number;
}

export interface TankEmptyingResult {
  tankAreaM2: number;
  outletAreaM2: number;
  initialFlowM3PerHour: number;
  emptyingTimeSeconds: number;
  emptyingTimeMinutes: number;
  points: TankEmptyingPoint[];
}

const circleArea = (diameterM: number) =>
  (Math.PI * diameterM * diameterM) / 4;

export const calculateTankEmptying = (
  input: TankEmptyingInputs,
  numberOfPoints = 161
): TankEmptyingResult | null => {
  const values = Object.values(input);
  if (
    values.some((value) => !Number.isFinite(value) || value <= 0) ||
    numberOfPoints < 2
  ) {
    return null;
  }

  const tankDiameterM = input.tankDiameterMm / 1000;
  const outletDiameterM = input.outletDiameterMm / 1000;
  const initialLevelM = input.initialLevelMm / 1000;
  const tankAreaM2 = circleArea(tankDiameterM);
  const outletAreaM2 = circleArea(outletDiameterM);
  const velocityFactor = Math.sqrt(2 * input.gravity);
  const k =
    (input.dischargeCoefficient * outletAreaM2 * velocityFactor) / tankAreaM2;
  const emptyingTimeSeconds = (2 * Math.sqrt(initialLevelM)) / k;
  const initialFlowM3PerHour =
    input.dischargeCoefficient *
    outletAreaM2 *
    Math.sqrt(2 * input.gravity * initialLevelM) *
    3600;

  const points = Array.from({ length: numberOfPoints }, (_, index) => {
    const timeSeconds =
      (emptyingTimeSeconds * index) / (numberOfPoints - 1);
    const remainingRoot = Math.max(
      0,
      Math.sqrt(initialLevelM) - (k * timeSeconds) / 2
    );
    const liquidLevelM = remainingRoot * remainingRoot;
    const flowM3PerHour =
      input.dischargeCoefficient *
      outletAreaM2 *
      Math.sqrt(2 * input.gravity * liquidLevelM) *
      3600;

    return {
      timeSeconds,
      liquidLevelMm: liquidLevelM * 1000,
      flowM3PerHour,
    };
  });

  return {
    tankAreaM2,
    outletAreaM2,
    initialFlowM3PerHour,
    emptyingTimeSeconds,
    emptyingTimeMinutes: emptyingTimeSeconds / 60,
    points,
  };
};
